import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import noop from 'lodash/noop';
import { Link } from '@framework/router/Next/Link';
import { DEFAULT_EMPTY_MESSAGE } from '@framework/ui/Molecules/Tables/constants';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import Paginator from '@creditflowresearch/uilib/Paginator';
import LoginOrSignUpPopover from '@/creditflowresearch/ui/common/components/LoginOrSignUpPopover';

import styles from './linkTable.module.scss';
import floatStyles from '@creditflowresearch/uilib/styles/helpers/floats.module.scss';
import cardStyles from '@creditflowresearch/uilib/styles/card.module.scss';

const PAGINATOR_INNER_PADDING = 20;
const POPOVER_GAP_FROM_PAGINATOR = 4;

interface IProps {
  containerClassName?: string,
  tableClassName?: string,
  rowClassName?: string,
  emptyMessageClassName?: string,
  emptyMessageText?: string,
  withSpinner?: boolean,
  rowDataTest?: string,

  title?: string,
  headerActionsComponent?: React.ElementType,
  showColumnsHeaders?: boolean,

  onPageChange?: (page: { selected: number }) => void,
  onItemsPerPageChange?: (params: { value: number }) => void,
  getRoutePath?: (item: any) => string | void,

  columns: any[],
  data?: any[],
  isFetching: boolean,
  totalCount?: number,
  page?: number,
  perPage?: number,
  isItemsPerPageVisible?: boolean,
  paginatorDataTest?: string,
  dataTest?: string,
  isRestricted?: boolean,
}

const LinkTable = (
  {
    containerClassName = '',
    tableClassName = '',
    rowClassName = '',
    emptyMessageClassName = '',
    emptyMessageText = '',
    withSpinner = true,
    rowDataTest = '',
    dataTest = 'linkTable',

    title = '',
    headerActionsComponent: HeaderActions = null,
    showColumnsHeaders = true,

    data = [],
    columns,
    isFetching,
    totalCount = 0,
    page = 1,
    perPage = 25,
    isItemsPerPageVisible = true,
    paginatorDataTest = '',

    onPageChange = noop,
    onItemsPerPageChange = noop,
    getRoutePath = noop,

    isRestricted = false,
  }: IProps,
) => {
  const containerCls = cn(cardStyles.cardContainerNoSwadow, containerClassName);
  const tableCls = cn({ [styles.tableWithPaginatorContainer]: !!totalCount }, tableClassName);
  const rowCls = cn(styles.row, rowClassName);
  const paginatorCls = cn(styles.paginatorContainer);

  const renderCells = useCallback(
    (rowData, rowIndex) => columns.map(
      (
        {
          name,
          className,
          cellCallback,
          width,
          minWidth,
          maxWidth,
        },
      ) => {
        const cellData = get(rowData, name);
        return (
          <div
            key={ name }
            className={ cn(styles.cell, className) }
            style={ { width, minWidth, maxWidth } }
          >
            { cellCallback ? cellCallback({ rowData, rowIndex, cellData }) : cellData }
          </div>
        );
      },
    ),
    [columns],
  );

  const renderRow = useCallback(
    (item, i) => (
      <Link
        key={ item.id || i }
        // @ts-ignore
        to={ getRoutePath(item) }
        className={ rowCls }
        dataTest={ rowDataTest ? `${ rowDataTest }_${ i }` : `linkTableRow_${ i }` }
      >
        { renderCells(item, i) }
      </Link>
    ),
    [getRoutePath, renderCells],
  );

  const columnsHeaders = useMemo(
    () => columns.map(
      (
        {
          title,
          name,
          width,
          minWidth,
          maxWidth,
          className,
        },
      ) => (
        <div
          key={ name }
          className={ cn(styles.tableHeaderCell, className) }
          style={ { width, minWidth, maxWidth } }
        >
          { title }
        </div>
      ),
    ),
    [columns],
  );

  return (
    <div className={ containerCls } data-test={ dataTest }>
      { (title || HeaderActions) && (
        <div className={ cn(cardStyles.cardInner, styles.stripe) }>
          <div className={ styles.tableHeader }>
            { title && (
              <div className={ styles.headerTitle }>
                <h3>{ title }</h3>
              </div>
            ) }
            { HeaderActions && (
              <div className={ floatStyles.pullRight }>
                <HeaderActions />
              </div>
            ) }
          </div>
        </div>
      ) }
      { withSpinner && (
        <Spinner
          overlay
          isVisible={ isFetching }
        />
      ) }
      <div className={ tableCls }>
        { showColumnsHeaders && (
          <div className={ styles.tableHeadersContainer }>
            { columnsHeaders }
          </div>
        ) }
        {
          (!data.length && !isFetching)
            ? (
              <div className={ cn(styles.emptyMessage, emptyMessageClassName) }>
                { emptyMessageText || DEFAULT_EMPTY_MESSAGE }
              </div>
            )
            : data.map(renderRow)
        }
      </div>
      <div className={ paginatorCls }>
        <LoginOrSignUpPopover
          isRestricted={ isRestricted }
          placement="bottom-end"
          isBlockLevelTrigger
          offsetOptions={ {
            mainAxis: POPOVER_GAP_FROM_PAGINATOR - PAGINATOR_INNER_PADDING,
            alignmentAxis: PAGINATOR_INNER_PADDING,
          } }
        >
          <Paginator
            isItemsPerPageVisible={ isItemsPerPageVisible }
            onPageChange={ onPageChange }
            onItemsPerPageChange={ onItemsPerPageChange }
            totalCount={ totalCount }
            page={ page }
            perPage={ perPage }
            dataTest={ paginatorDataTest || 'linkTablePaginator' }
            isDisabled={ isRestricted }
          />
        </LoginOrSignUpPopover>
      </div>
    </div>
  );
};

export default LinkTable;
